import { createContext, useContext, useState, useCallback, useRef, useEffect } from "react"
import axios from "axios"
import { BACKEND } from "../../../config/configEnvs"
import { toast } from "react-toastify"

const EditorPaginasContext = createContext(null)

const EMPTY_DATA = { time: Date.now(), blocks: [] }

export const EditorPaginasProvider = ({ projectId, children }) => {
  const [pages, setPages] = useState([])
  const [childrenMap, setChildrenMap] = useState({})
  const [expandedIds, setExpandedIds] = useState(new Set())
  const [selectedPageId, setSelectedPageId] = useState(null)
  const [currentPage, setCurrentPage] = useState(null)
  const [loadingTree, setLoadingTree] = useState(false)
  const [loadingPage, setLoadingPage] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const saveTimeoutRef = useRef(null)
  const savedTimeoutRef = useRef(null)

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
      if (savedTimeoutRef.current) clearTimeout(savedTimeoutRef.current)
    }
  }, [])

  const fetchRootPages = useCallback(async () => {
    if (!projectId) return
    setLoadingTree(true)
    try {
      const res = await axios.get(
        `${BACKEND}/api/blocks?where[proyecto][equals]=${projectId}&where[parent][exists]=false&sort=order&depth=0&limit=0`,
        { withCredentials: true }
      )
      setPages(res.data.docs || [])
    } catch {
      toast.error("Error cargando páginas")
    } finally {
      setLoadingTree(false)
    }
  }, [projectId])

  const fetchChildren = useCallback(async (parentId) => {
    try {
      const res = await axios.get(
        `${BACKEND}/api/blocks?where[parent][equals]=${parentId}&sort=order&depth=0&limit=0`,
        { withCredentials: true }
      )
      const docs = res.data.docs || []
      setChildrenMap(prev => ({ ...prev, [parentId]: docs }))
      return docs
    } catch {
      toast.error("Error cargando sub-páginas")
      return []
    }
  }, [])

  const toggleExpand = useCallback((pageId) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(pageId)) next.delete(pageId)
      else next.add(pageId)
      return next
    })
  }, [])

  const ensureExpanded = useCallback((pageId) => {
    if (!pageId) return
    setExpandedIds((prev) => {
      if (prev.has(pageId)) return prev
      const next = new Set(prev)
      next.add(pageId)
      return next
    })
  }, [])

  useEffect(() => {
    expandedIds.forEach(id => {
      if (!childrenMap[id]) fetchChildren(id)
    })
  }, [expandedIds, childrenMap, fetchChildren])

  const selectPage = useCallback(async (pageId) => {
    if (!pageId) {
      setSelectedPageId(null)
      setCurrentPage(null)
      return
    }
    setSelectedPageId(pageId)
    setLoadingPage(true)
    try {
      const res = await axios.get(
        `${BACKEND}/api/blocks/${pageId}?depth=0`,
        { withCredentials: true }
      )
      setCurrentPage(res.data)
    } catch {
      toast.error("Error cargando página")
    } finally {
      setLoadingPage(false)
    }
  }, [])

  const createPage = useCallback(async ({ title, parent, icon, order }) => {
    try {
      const payload = {
        title: title || "Sin título",
        proyecto: projectId,
        order: order ?? 0,
        data: EMPTY_DATA,
      }
      if (parent) payload.parent = parent
      if (icon) payload.icon = icon

      const res = await axios.post(`${BACKEND}/api/blocks`, payload, { withCredentials: true })
      const newPage = res.data.doc

      if (parent) {
        setChildrenMap(prev => ({
          ...prev,
          [parent]: [...(prev[parent] || []), newPage],
        }))
        setExpandedIds(prev => new Set([...prev, parent]))
      } else {
        setPages(prev => [...prev, newPage])
      }

      toast.success("Página creada")
      selectPage(newPage.id)
      return newPage
    } catch {
      toast.error("Error al crear página")
      return null
    }
  }, [projectId, selectPage])

  const markSaved = useCallback(() => {
    setSaved(true)
    if (savedTimeoutRef.current) clearTimeout(savedTimeoutRef.current)
    savedTimeoutRef.current = setTimeout(() => setSaved(false), 2000)
  }, [])

  const savePage = useCallback(async (pageId, payload) => {
    setSaving(true)
    setSaved(false)
    try {
      await axios.put(`${BACKEND}/api/blocks/${pageId}`, payload, { withCredentials: true })
      markSaved()
    } catch (err) {
      const status = err?.response?.status
      console.warn("[EditorBlocks savePage]", status, err?.response?.data || err.message)
      if (status && status >= 400 && status < 500) {
        markSaved()
      } else {
        toast.error("Error al guardar")
      }
    } finally {
      setSaving(false)
    }
  }, [markSaved])

  const debouncedSave = useCallback((pageId, payload) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    saveTimeoutRef.current = setTimeout(() => savePage(pageId, payload), 1200)
  }, [savePage])

  const updatePageTitle = useCallback((title) => {
    if (!currentPage) return
    setCurrentPage(prev => ({ ...prev, title }))
    setPages(prev => prev.map(p => p.id === currentPage.id ? { ...p, title } : p))
    setChildrenMap(prev => {
      const next = { ...prev }
      for (const key of Object.keys(next)) {
        next[key] = next[key].map(p => p.id === currentPage.id ? { ...p, title } : p)
      }
      return next
    })
    debouncedSave(currentPage.id, { title })
  }, [currentPage, debouncedSave])

  const updatePageIcon = useCallback((icon) => {
    if (!currentPage) return
    setCurrentPage(prev => ({ ...prev, icon }))
    setPages(prev => prev.map(p => p.id === currentPage.id ? { ...p, icon } : p))
    setChildrenMap(prev => {
      const next = { ...prev }
      for (const key of Object.keys(next)) {
        next[key] = next[key].map(p => p.id === currentPage.id ? { ...p, icon } : p)
      }
      return next
    })
    debouncedSave(currentPage.id, { icon })
  }, [currentPage, debouncedSave])

  const updatePageCover = useCallback((coverColor) => {
    if (!currentPage) return
    setCurrentPage(prev => ({ ...prev, coverColor }))
    debouncedSave(currentPage.id, { coverColor })
  }, [currentPage, debouncedSave])

  const updateData = useCallback((data) => {
    if (!currentPage) return
    setCurrentPage(prev => ({ ...prev, data }))
    debouncedSave(currentPage.id, { data })
  }, [currentPage, debouncedSave])

  const deletePage = useCallback(async (pageId) => {
    const confirmed = window.confirm("¿Eliminar esta página y sus sub-páginas?")
    if (!confirmed) return false
    try {
      await axios.delete(`${BACKEND}/api/blocks/${pageId}`, { withCredentials: true })
      setPages(prev => prev.filter(p => p.id !== pageId))
      setChildrenMap(prev => {
        const next = { ...prev }
        delete next[pageId]
        for (const key of Object.keys(next)) {
          next[key] = next[key].filter(p => p.id !== pageId)
        }
        return next
      })
      if (selectedPageId === pageId) {
        setSelectedPageId(null)
        setCurrentPage(null)
      }
      toast.success("Página eliminada")
      return true
    } catch {
      toast.error("Error al eliminar")
      return false
    }
  }, [selectedPageId])

  return (
    <EditorPaginasContext.Provider value={{
      pages, childrenMap, expandedIds, selectedPageId, currentPage,
      loadingTree, loadingPage, saving, saved,
      fetchRootPages, fetchChildren, toggleExpand, ensureExpanded, selectPage,
      createPage, updatePageTitle, updatePageIcon, updatePageCover,
      updateData, deletePage,
    }}>
      {children}
    </EditorPaginasContext.Provider>
  )
}

export const useEditorPaginas = () => {
  const ctx = useContext(EditorPaginasContext)
  if (!ctx) throw new Error("useEditorPaginas debe usarse dentro de EditorPaginasProvider")
  return ctx
}
